import React, { useState, useEffect } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { FaUserCircle, FaBars, FaKey, FaSignOutAlt } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import NotificationBell from '../../components/notifications/NotificationBell';

const InstructorTopbar = ({ onMenuClick }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userDisplayData, setUserDisplayData] = useState({
    name: 'Instructor',
    role: 'Instructor',
    initials: 'I'
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      const displayName = (user.first_name && user.last_name)
        ? `${user.first_name} ${user.last_name}`
        : user.email || 'Instructor';
      const initials = (user.first_name && user.last_name)
        ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
        : user.email?.charAt(0).toUpperCase() || 'I';
      setUserDisplayData({ name: displayName, role: 'Instructor', initials });
    }
  }, [user]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/', { replace: true });
  };

  const handleNavigate = (path) => {
    setShowUserMenu(false);
    navigate(path);
  };

  return (
    <div className="sticky top-0 z-50 border-b brand-app-header-border shadow-lg backdrop-blur-sm brand-app-header">
      <div className="py-2 px-3 md:py-3">
        <div className="flex items-center justify-between gap-3 max-w-7xl mx-auto">

          {/* LEFT: Hamburger + Title */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Button
              bsPrefix="custom-hamburger"
              className="!bg-transparent !border-none !text-white d-md-none p-1.5 rounded-lg hover:bg-white/20 transition-all duration-200 [&_.btn]:hidden"
              onClick={onMenuClick}
              aria-label="Toggle menu"
            >
              <FaBars className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-white truncate drop-shadow-sm">
                Enable LXP
              </div>
              <div className="hidden md:block text-sm text-gray-100 font-medium">
                Instructor Panel
              </div>
            </div>
          </div>

          {/* RIGHT: Bell + User */}
          <div className="flex items-center gap-2 sm:gap-3">

            <div className="flex items-center justify-center">
              <NotificationBell />
            </div>

            <Dropdown align="end" show={showUserMenu} onToggle={setShowUserMenu}>
              <Dropdown.Toggle
                as="button"
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all duration-200 border border-white/30 sm:border sm:hover:bg-white/30 sm:bg-white/20 sm:flex sm:items-center sm:gap-2 sm:px-3 sm:py-1.5 sm:rounded-lg sm:text-white sm:transition after:!content-none shadow-sm hover:shadow-md"
                aria-label="User menu"
              >
                <span className="rounded-full bg-white/30 text-white flex items-center justify-center font-semibold text-sm w-7 h-7 sm:w-8 sm:h-8 sm:text-base shadow-sm">
                  {userDisplayData.initials}
                </span>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-sm font-semibold text-white">{userDisplayData.name}</div>
                  <div className="text-xs text-gray-100 font-medium">{userDisplayData.role}</div>
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu
                className="mt-2 shadow-2xl rounded-2xl border-0 bg-white/98 backdrop-blur-xl p-3 min-w-[280px] overflow-hidden"
                align="end"
              >
                <div className="px-2 py-2 mb-2 border-b border-gray-100">
                  <div className="flex flex-col">
                    <strong className="text-gray-800 text-sm font-semibold" style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {userDisplayData.name}
                    </strong>
                    <small className="text-gray-500 text-xs mt-0.5" style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.email}
                    </small>
                  </div>
                </div>

                <div
                  role="menuitem"
                  className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-purple-50/90 transition-all duration-200 cursor-pointer group whitespace-nowrap"
                  onClick={() => handleNavigate('/instructor/profile')}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNavigate('/instructor/profile'); } }}
                  aria-label="View profile"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-md group-hover:shadow-purple-500/50 group-hover:scale-110 transition-all duration-200 flex items-center justify-center">
                    <FaUserCircle className="w-5 h-5" />
                  </div>
                  <span className="font-medium text-gray-800 group-hover:text-purple-700 text-sm">Profile</span>
                </div>

                <div
                  role="menuitem"
                  className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-purple-50/90 transition-all duration-200 cursor-pointer group whitespace-nowrap"
                  onClick={() => handleNavigate('/instructor/change-password')}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNavigate('/instructor/change-password'); } }}
                  aria-label="Change password"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-md group-hover:shadow-purple-500/50 group-hover:scale-110 transition-all duration-200 flex items-center justify-center">
                    <FaKey className="w-5 h-5" />
                  </div>
                  <span className="font-medium text-gray-800 group-hover:text-purple-700 text-sm">Change Password</span>
                </div>

                <hr className="my-2 border-0 h-px bg-gradient-to-r from-transparent via-purple-200 to-transparent" />

                <div
                  role="menuitem"
                  onClick={handleLogout}
                  className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-red-50/90 transition-all duration-200 cursor-pointer group whitespace-nowrap"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLogout(); } }}
                  aria-label="Sign out"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-md group-hover:shadow-red-500/50 group-hover:scale-110 transition-all duration-200 flex items-center justify-center">
                    <FaSignOutAlt className="w-5 h-5" />
                  </div>
                  <span className="font-bold text-red-600 group-hover:text-red-700 text-sm">Sign Out</span>
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorTopbar;
